import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, Divider, FormControlLabel,
  MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography } from '@mui/material';
import { collection, collectionGroup, getDocs, onSnapshot, orderBy, query,
  DocumentData, QueryDocumentSnapshot,
  serverTimestamp, setDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, functions, storage } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { UI } from './settingsUi';

type Language = 'en' | 'gu' | 'hi';
type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
type StudioButton = { type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'; text: string; url?: string; phone_number?: string };
type StudioTemplate = {
  id?: string; name: string; language: Language; category: 'MARKETING' | 'UTILITY';
  headerFormat: HeaderFormat; headerText: string; mediaUrl: string;
  mediaFilename: string; samplePath?: string; latitude: string; longitude: string;
  locationName: string; locationAddress: string; body: string; footer: string;
  buttons: StudioButton[]; updatedAt?: any;
};
type Contact = { id: string; source: 'lead' | 'buyer' | 'broker' | 'contact-book';
  phone: string; name: string; projectId: string; projectName: string;
  status: string; active: boolean };
type CsvRow = { phone: string; csvName: string; fields: Record<string, string>; row: number };
type Recipient = { phone: string; name: string; source: string; projectId: string;
  projectName: string; fields: Record<string, string> };
type Mapping = { source: '' | 'name' | 'project' | 'csv' | 'fixed'; key: string; fixed: string };
type Campaign = { id: string; name: string; status: string; recipientCount: number;
  sentCount?: number; failedCount?: number; skippedCount?: number; sendAt?: any };

const blankTemplate = (): StudioTemplate => ({name: '', language: 'en',
  category: 'MARKETING', headerFormat: 'NONE', headerText: '', mediaUrl: '',
  mediaFilename: '', latitude: '', longitude: '', locationName: '',
  locationAddress: '', body: '', footer: '', buttons: []});
const phoneOf = (value: unknown) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits.length === 10 ? `91${digits}` :
    digits.length === 12 && digits.startsWith('91') ? digits : '';
};
const parseCsv = (input: string): string[][] => {
  const rows: string[][] = []; let row: string[] = []; let field = ''; let quoted = false;
  const text = input.replace(/^\uFEFF/, '');
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"' && quoted && text[i + 1] === '"') { field += '"'; i++; }
    else if (ch === '"') quoted = !quoted;
    else if (ch === ',' && !quoted) { row.push(field); field = ''; }
    else if ((ch === '\n' || ch === '\r') && !quoted) {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); if (row.some((v) => v.trim())) rows.push(row);
      row = []; field = '';
    } else field += ch;
  }
  row.push(field); if (row.some((v) => v.trim())) rows.push(row);
  if (quoted) throw new Error('CSV has an unclosed quoted field.');
  return rows;
};
const variableCount = (body: string) => {
  const positions = Array.from(body.matchAll(/\{\{(\d+)\}\}/g), (m) => Number(m[1]));
  return Math.max(0, ...positions);
};
const validateVariables = (body: string) => {
  const count = variableCount(body);
  const positions = Array.from(body.matchAll(/\{\{(\d+)\}\}/g), (m) => Number(m[1]));
  if (count > 20 || positions.some((n) => n < 1) ||
      (count && new Set(positions).size !== count))
    throw new Error('Use sequential variables: {{1}}, {{2}}, …');
  return count;
};
const LANG: Record<Language, string> = {en: 'English', gu: 'ગુજરાતી', hi: 'हिंदी'};
const SAMPLE_CSV = 'phone,name,project,collection_date,collection_time,location\r\n' +
  '+91XXXXXXXXXX,Example Customer,Swarna Avsar,05 Nov 2026,11:00 am,Swarna Avsar office\r\n' +
  '+91YYYYYYYYYY,Example Contact,Swarna Lagom,06 Nov 2026,4:00 pm,Swarna Lagom office\r\n';
const downloadSampleCsv = () => {
  const url = URL.createObjectURL(new Blob([SAMPLE_CSV], {type: 'text/csv;charset=utf-8'}));
  const link = document.createElement('a');
  link.href = url; link.download = 'whatsapp-studio-sample.csv';
  document.body.appendChild(link); link.click(); link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const boxSx = {p: 2, border: '1px solid #e7e0d7', borderRadius: 2, bgcolor: '#fff'};
const studioSx = {
  maxWidth: 1160,
  '& .MuiButton-root': {textTransform: 'none', borderRadius: 1.5, fontWeight: 600,
    boxShadow: 'none', minHeight: 36},
  '& .MuiButton-contained': {bgcolor: UI.gold, color: '#fff',
    '&:hover': {bgcolor: UI.goldHover, boxShadow: 'none'}},
  '& .MuiButton-outlined': {borderColor: UI.gold, color: UI.gold,
    '&:hover': {borderColor: UI.goldHover, bgcolor: UI.goldTint}},
  '& .MuiButton-text': {color: UI.gold, '&:hover': {bgcolor: UI.goldTint}},
  '& .MuiButton-error': {color: '#b42318'},
  '& .MuiOutlinedInput-root': {borderRadius: 1.5, bgcolor: '#fff'},
};
const first = (...values: unknown[]) => values.map((value) => String(value || '').trim()).find(Boolean) || '';
const firstName = (name: string) => name.trim().split(/\s+/)[0] || '';
const blankMapping = (): Mapping => ({source: '', key: '', fixed: ''});
const preferredNames = (matches: Contact[]) => {
  const eligible = matches.filter((c) => c.active);
  const buyers = Array.from(new Set(eligible.filter((c) => c.source === 'buyer').map((c) => c.name)));
  if (buyers.length) return {names: buyers, source: 'Unit customer'};
  const book = Array.from(new Set(eligible.filter((c) => c.source === 'contact-book').map((c) => c.name)));
  return {names: book, source: 'Contact Book'};
};
const automaticName = (names: string[], csvName = '') => {
  const givenNames = Array.from(new Set(names.map(firstName).filter(Boolean)));
  return givenNames.length > 1 ? '' : givenNames[0] || firstName(csvName) || 'there';
};
const localStudioContacts = async (): Promise<Contact[]> => {
  const sources = await Promise.allSettled([
    getDocs(collection(db, 'leads')),
    getDocs(collectionGroup(db, 'customers')),
    getDocs(collection(db, 'brokers')),
    getDocs(collection(db, 'contactsBook')),
    getDocs(collection(db, 'projects')),
    getDocs(collection(db, 'leadStatuses')),
  ]);
  if (sources.slice(0, 4).every((result) => result.status === 'rejected'))
    throw new Error('CRM contacts could not be read.');
  const docs = (index: number): QueryDocumentSnapshot<DocumentData>[] => {
    const result = sources[index];
    return result.status === 'fulfilled' ? result.value.docs : [];
  };
  const projectNames = new Map(docs(4).map((row) =>
    [row.id, first(row.data().name, row.data().title, row.id)]));
  const statusNames = new Map(docs(5).map((row) =>
    [row.id, first(row.data().label, row.data().name, row.id)]));
  const output: Contact[] = [];
  const add = (source: Contact['source'], id: string, phoneValue: unknown,
    nameValue: unknown, projectId: string, status = '', active = true) => {
    const phone = phoneOf(phoneValue); const name = String(nameValue || '').trim();
    if (phone && name) output.push({id, source, phone, name, projectId,
      projectName: projectNames.get(projectId) || '', status, active});
  };
  docs(0).forEach((row) => {const x = row.data();
    const status = statusNames.get(String(x.status || '')) || String(x.status || '');
    add('lead', row.id, first(x.phone, x.phone10, x.mobile),
      first(x.name, x.fullName), String(x.projectId || ''), status,
      !x.whatsappOptedOut && !['closed', 'lost', 'not interested', 'cancelled', 'won'].includes(status.toLowerCase()));
  });
  docs(1).forEach((row) => {const x = row.data(); const path = row.ref.path.split('/');
    const at = path.indexOf('projects'); const projectId = at >= 0 ? path[at + 1] || '' : '';
    add('buyer', row.ref.path,
      first(x.whatsappNumber, x.mobileNo, x.phoneE164, x.mobileE164, x.phone,
        x.mobile, x.phoneNumber, x.contactNumber, `${x.phoneCountryCode || ''}${x.phoneNational || ''}`),
      first(x.displayName, x.fullName, x.customerName, x.allotteeName, x.name,
        [x.firstName, x.middleName, x.surname || x.lastName].filter(Boolean).join(' ')),
      projectId, '', x.active !== false);
  });
  docs(2).forEach((row) => {const x = row.data();
    add('broker', row.id, first(x.phoneE164, x.mobileNo, x.mobile, x.phone,
      `${x.phoneCountryCode || ''}${x.phoneNational || ''}`),
    first(x.brokerName, x.displayName, x.name), '', '', x.active !== false);
  });
  docs(3).forEach((row) => {const x = row.data();
    add('contact-book', row.id,
      first(x.phone, x.phoneE164, x.whatsappNumber, x.phone10, x.mobile),
      first(x.name, x.displayName), String(x.projectId || ''),
      first(x.type), x.active !== false);
  });
  return output;
};
const renderWhatsApp = (body: string): React.ReactNode[] => body.split(
  /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|```[^`]+```)/g
).map((part, index) => {
  if (part.startsWith('```') && part.endsWith('```'))
    return <code key={index}>{part.slice(3, -3)}</code>;
  if (part.startsWith('*') && part.endsWith('*'))
    return <strong key={index}>{part.slice(1, -1)}</strong>;
  if (part.startsWith('_') && part.endsWith('_'))
    return <em key={index}>{part.slice(1, -1)}</em>;
  if (part.startsWith('~') && part.endsWith('~'))
    return <s key={index}>{part.slice(1, -1)}</s>;
  return part;
});

export default function WhatsappStudioTab() {
  const { currentUser } = useAuth();
  const [templates, setTemplates] = useState<StudioTemplate[]>([]);
  const [submissions, setSubmissions] = useState<Record<string, string>>({});
  const [submissionIds, setSubmissionIds] = useState<Record<string, string>>({});
  const [liveStatuses, setLiveStatuses] = useState<Record<string, string>>({});
  const [liveCategories, setLiveCategories] = useState<Record<string, string>>({});
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignDetailId, setCampaignDetailId] = useState('');
  const [campaignDetails, setCampaignDetails] = useState<Array<{phone: string;
    name: string; status: string; error?: string; waMessageId?: string}>>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [projects, setProjects] = useState<Array<{id: string; name: string}>>([]);
  const [editor, setEditor] = useState<StudioTemplate>(blankTemplate());
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [activeId, setActiveId] = useState('');
  const [mode, setMode] = useState<'create' | 'campaign'>('create');
  const [audienceSource, setAudienceSource] = useState<'csv' | Contact['source']>('csv');
  const [csvRows, setCsvRows] = useState<CsvRow[]>([]);
  const [csvTable, setCsvTable] = useState<string[][]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [phoneColumn, setPhoneColumn] = useState('');
  const [nameColumn, setNameColumn] = useState('');
  const [nameChoices, setNameChoices] = useState<Record<string, string>>({});
  const [selectedPhones, setSelectedPhones] = useState<Record<string, boolean>>({});
  const [projectFilter, setProjectFilter] = useState('');
  const [contactTypeFilter, setContactTypeFilter] = useState('');
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [headerMapping, setHeaderMapping] = useState<Mapping>(blankMapping());
  const [buttonMappings, setButtonMappings] = useState<Record<number, Mapping>>({});
  const [campaignName, setCampaignName] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadContacts = useCallback(async () => {
    let rows: Contact[];
    try {
      const call = httpsCallable(functions, 'whatsappStudioContacts');
      const result: any = await call({});
      rows = Array.isArray(result.data?.contacts) ? result.data.contacts : [];
    } catch {
      rows = await localStudioContacts();
    }
    setContacts(rows);
    setContactsLoaded(true);
  }, []);
  useEffect(() => {
    const unsubTemplates = onSnapshot(query(collection(db, 'whatsappStudioTemplates'),
      orderBy('updatedAt', 'desc')), (snap) => setTemplates(snap.docs.map((d) =>
      ({id: d.id, ...d.data()} as StudioTemplate))));
    const unsubSubmissions = onSnapshot(collection(db, 'whatsappTemplateSubmissions'),
      (snap) => {
        setSubmissions(Object.fromEntries(snap.docs.map((d) =>
          [d.id, String(d.data().status || '')])));
        setSubmissionIds(Object.fromEntries(snap.docs.map((d) =>
          [d.id, String(d.data().templateId || '')])));
      });
    const unsubCampaigns = onSnapshot(query(collection(db, 'whatsappStudioCampaigns'),
      orderBy('createdAt', 'desc')), (snap) => setCampaigns(snap.docs.map((d) =>
        ({id: d.id, ...d.data()} as Campaign))));
    getDocs(collection(db, 'projects')).then((p) => {
      setProjects(p.docs.map((d) => ({id: d.id,
        name: String(d.data().name || d.data().title || d.id)})));
    }).catch((e) => setError(String(e.message || e)));
    loadContacts().catch((e) => setError(
      `Could not load Studio contacts. ${String(e.message || e)}`));
    const fetchMeta = async () => {
      try {
        const result: any = await httpsCallable(functions, 'whatsappFetchTemplates')({});
        const rows = Array.isArray(result.data?.templates) ? result.data.templates : [];
        setLiveStatuses(Object.fromEntries(rows.map((r: any) =>
          [`${r.name}__${r.language}`, String(r.status || '')])));
        setLiveCategories(Object.fromEntries(rows.map((r: any) =>
          [`${r.name}__${r.language}`, String(r.category || '')])));
      } catch (e: any) { setError(`Could not load Meta approval: ${String(e.message || e)}`); }
    };
    fetchMeta();
    const metaTimer = window.setInterval(fetchMeta, 60000);
    return () => { window.clearInterval(metaTimer);
      unsubTemplates(); unsubSubmissions(); unsubCampaigns(); };
  }, [loadContacts]);

  const approvedTemplates = templates.filter((t) =>
    (liveStatuses[`${t.name}__${t.language}`] ||
      submissions[`${t.name}__${t.language}`])?.toUpperCase() === 'APPROVED')
    .map((t) => ({...t, category:
      (liveCategories[`${t.name}__${t.language}`] || t.category) as StudioTemplate['category']}));
  const selectedTemplate = approvedTemplates.find((t) => t.id === activeId) || null;
  const selectedStatus = selectedTemplate ?
    liveStatuses[`${selectedTemplate.name}__${selectedTemplate.language}`] ||
    submissions[`${selectedTemplate.name}__${selectedTemplate.language}`] || 'NOT SUBMITTED' : '';
  const approved = selectedStatus.toUpperCase() === 'APPROVED' &&
    (!selectedTemplate || !liveCategories[`${selectedTemplate.name}__${selectedTemplate.language}`] ||
      liveCategories[`${selectedTemplate.name}__${selectedTemplate.language}`] === selectedTemplate.category);
  const deliveryDetailsReady = !selectedTemplate || (
    (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplate.headerFormat) ||
      /^https:\/\//i.test(selectedTemplate.mediaUrl)) &&
    (selectedTemplate.headerFormat !== 'LOCATION' ||
      (!!selectedTemplate.latitude && !!selectedTemplate.longitude))
  );
  const byPhone = useMemo(() => {
    const map = new Map<string, Contact[]>();
    contacts.forEach((c) => map.set(c.phone, [...(map.get(c.phone) || []), c]));
    return map;
  }, [contacts]);
  const csvPreview = useMemo(() => csvRows.map((r) => {
    const matches = byPhone.get(r.phone) || [];
    const {names, source} = preferredNames(matches);
    const autoName = automaticName(names, r.csvName);
    const choice = nameChoices[r.phone];
    return {...r, matches, names, source, autoName,
      name: choice ? firstName(choice) : autoName};
  }), [csvRows, byPhone, nameChoices]);
  const availableContacts = useMemo(() => contacts.filter((c) =>
    c.source === audienceSource && c.active &&
    (!projectFilter || c.projectId === projectFilter) &&
    (!['lead', 'contact-book'].includes(audienceSource) ||
      !contactTypeFilter || c.status === contactTypeFilter)),
  [contacts, audienceSource, projectFilter, contactTypeFilter]);
  const audience = useMemo<Recipient[]>(() => {
    if (audienceSource === 'csv') return csvPreview.map((r) => {
      const ids = Array.from(new Set(r.matches.map((m) => m.projectId).filter(Boolean)));
      const project = ids.length === 1 ? r.matches.find((m) => m.projectId === ids[0]) : null;
      return {phone: r.phone, name: r.name, source: 'csv',
        projectId: project?.projectId || '', projectName: project?.projectName || '',
        fields: r.fields};
    });
    const unique = new Map<string, Contact[]>();
    availableContacts.forEach((c) => {
      if (selectedPhones[c.phone]) unique.set(c.phone, [...(unique.get(c.phone) || []), c]);
    });
    return Array.from(unique.entries()).map(([phone, matches]) => {
      const names = audienceSource === 'broker' ?
        Array.from(new Set(matches.map((c) => c.name))) :
        preferredNames(byPhone.get(phone) || []).names;
      const projectIds = Array.from(new Set(matches.map((c) => c.projectId).filter(Boolean)));
      const project = projectIds.length === 1 ? matches.find((c) => c.projectId === projectIds[0]) : null;
      return {phone, name: nameChoices[phone] ? firstName(nameChoices[phone]) : automaticName(names),
        source: audienceSource, projectId: project?.projectId || '',
        projectName: project?.projectName || '', fields: {}};
    });
  }, [audienceSource, csvPreview, availableContacts, selectedPhones, nameChoices, byPhone]);
  const selectedConflicts = useMemo(() => {
    if (audienceSource === 'csv') return [];
    const grouped = new Map<string, string[]>();
    availableContacts.filter((c) => selectedPhones[c.phone])
      .forEach((c) => grouped.set(c.phone, [...(grouped.get(c.phone) || []), c.name]));
    return Array.from(grouped.entries()).map(([phone, names]) =>
      ({phone, names: audienceSource === 'broker' ? Array.from(new Set(names)) :
        preferredNames(byPhone.get(phone) || []).names}))
      .filter((r) => new Set(r.names.map(firstName)).size > 1);
  }, [audienceSource, availableContacts, selectedPhones, byPhone]);
  const count = selectedTemplate ? variableCount(selectedTemplate.body) : 0;
  const isGiftCollectionTemplate = selectedTemplate?.name === 'diwali_gift_collection_2026_v3';
  const resolveMapping = (r: Recipient, mapping: Mapping) =>
    mapping.source === 'name' ? r.name :
      mapping.source === 'project' ? r.projectName :
        mapping.source === 'csv' ? r.fields[mapping.key] || '' :
          mapping.source === 'fixed' ? mapping.fixed.trim() : '';
  const mappingReady = (mapping?: Mapping) => !!mapping?.source &&
    (mapping.source !== 'fixed' || !!mapping.fixed.trim()) &&
    (mapping.source !== 'csv' || (audienceSource === 'csv' && !!mapping.key));
  const mappingsReady = Array.from({length: count}, (_, i) => mappingReady(mappings[i])).every(Boolean) &&
    (selectedTemplate?.headerFormat !== 'TEXT' || !selectedTemplate.headerText.includes('{{1}}') ||
      mappingReady(headerMapping)) &&
    (selectedTemplate?.buttons || []).every((b, i) =>
      b.type !== 'URL' || !b.url?.includes('{{1}}') || mappingReady(buttonMappings[i]));
  const resolved = useMemo(() => audience.map((r) => ({...r,
    values: Array.from({length: count}, (_, i) =>
      resolveMapping(r, mappings[i] || blankMapping())),
    headerValue: selectedTemplate?.headerFormat === 'TEXT' &&
      selectedTemplate.headerText.includes('{{1}}') ? resolveMapping(r, headerMapping) : '',
    buttonValues: (selectedTemplate?.buttons || []).map((b, i) =>
      b.type === 'URL' && b.url?.includes('{{1}}') ?
        resolveMapping(r, buttonMappings[i] || blankMapping()) : ''),
  })), [audience, count, mappings, headerMapping, buttonMappings, selectedTemplate]);
  const invalid = resolved.filter((r) => !r.name || r.values.some((v) => !v.trim()) ||
    (selectedTemplate?.headerFormat === 'TEXT' && selectedTemplate.headerText.includes('{{1}}') && !r.headerValue.trim()) ||
    (selectedTemplate?.buttons || []).some((b, i) => b.type === 'URL' && b.url?.includes('{{1}}') && !r.buttonValues[i]?.trim()));
  const mediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(editor.headerFormat);
  const mappingEditor = (label: string, mapping: Mapping,
    update: (next: Mapping) => void, fixedLabel = 'Value') =>
    <Stack direction={{xs: 'column', sm: 'row'}} gap={1}>
      <Typography sx={{minWidth: 110, pt: 1}}>{label}</Typography>
      <Select value={mapping.source} displayEmpty onChange={(e) => update({
        source: e.target.value as Mapping['source'], key: '', fixed: ''})}>
        <MenuItem value="" disabled>Choose value source</MenuItem>
        <MenuItem value="name">Resolved first name</MenuItem>
        <MenuItem value="project">Project name</MenuItem>
        {audienceSource === 'csv' && <MenuItem value="csv">CSV column</MenuItem>}
        <MenuItem value="fixed">Same value for all</MenuItem>
      </Select>
      {mapping.source === 'csv' && <Select value={mapping.key} displayEmpty
        onChange={(e) => update({...mapping, key: String(e.target.value)})}>
          <MenuItem value="">Choose CSV column</MenuItem>
          {csvHeaders.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
        </Select>}
      {mapping.source === 'fixed' && <TextField label={fixedLabel} value={mapping.fixed}
        onChange={(e) => update({...mapping, fixed: e.target.value})} />}
    </Stack>;

  const onCsv = async (file: File) => {
    try {
      setCsvRows([]); setCsvHeaders([]); setCsvTable([]);
      const table = parseCsv(await file.text());
      if (table.length < 2) throw new Error('CSV needs a header and at least one contact.');
      setCsvTable(table);
      const headers = table[0].map((h) => h.trim());
      if (headers.some((h) => !h) || new Set(headers).size !== headers.length)
        throw new Error('CSV column names must be present and distinct.');
      setCsvHeaders(headers);
      const pc = headers.find((h) => /phone|mobile|whatsapp|number/i.test(h)) || headers[0];
      const nc = headers.find((h) => /^(name|full name|customer name|contact name)$/i.test(h)) || '';
      setPhoneColumn(pc); setNameColumn(nc);
      readCsvRows(table, headers, pc, nc);
      setNameChoices({}); setConsentConfirmed(false); setError('');
    } catch (e: any) { setError(String(e.message || e)); }
  };
  const readCsvRows = (table: string[][], headers: string[], pc: string, nc: string) => {
    const seen = new Set<string>(); const rows: CsvRow[] = [];
    table.slice(1).forEach((cells, index) => {
      const fields = Object.fromEntries(headers.map((h, i) => [h, String(cells[i] || '').trim()]));
      const phone = phoneOf(fields[pc]);
      if (!phone || seen.has(phone)) return;
      seen.add(phone);
      rows.push({phone, csvName: fields[nc] || '', fields, row: index + 2});
    });
    setCsvRows(rows);
    if (!rows.length) setError('No valid distinct Indian mobile numbers found.');
  };
  const changeCsvColumns = (pc: string, nc: string) => {
    setPhoneColumn(pc); setNameColumn(nc);
    readCsvRows(csvTable, csvHeaders, pc, nc);
  };

  const saveTemplate = async () => {
    setError(''); setNotice('');
    try {
      const body = editor.body.trim();
      if (!/^[a-z0-9_]{1,512}$/.test(editor.name)) throw new Error('Use lowercase letters, numbers and _ in the template name.');
      if (!body || body.length > 1024) throw new Error('Body must have 1–1024 characters.');
      validateVariables(body);
      if (/^\s*\{\{\d+\}\}|\{\{\d+\}\}\s*$/.test(body))
        throw new Error('Body cannot start or end with a variable.');
      if (editor.footer.length > 60) throw new Error('Footer can have at most 60 characters.');
      if (editor.buttons.length > 10 || editor.buttons.some((button) =>
        !button.text.trim() || button.text.length > 25 ||
        (button.type === 'URL' && !/^https:\/\//.test(button.url || '')) ||
        (button.type === 'PHONE_NUMBER' && !/^\+?\d{10,15}$/.test(button.phone_number || ''))))
        throw new Error('Use at most 10 buttons with labels, valid HTTPS URLs or phone numbers.');
      if (editor.headerFormat === 'TEXT' && !editor.headerText.trim()) throw new Error('Enter header text.');
      if (editor.headerFormat === 'TEXT' && /\{\{(?!1\}\})\d+\}\}/.test(editor.headerText))
        throw new Error('A text header can use only {{1}}.');
      if (mediaHeader && !sampleFile && !editor.mediaUrl) throw new Error('Upload a sample media file.');
      if (editor.headerFormat === 'LOCATION' && (!editor.latitude || !editor.longitude))
        throw new Error('Enter location coordinates.');
      setBusy(true);
      let mediaUrl = editor.mediaUrl; let samplePath = editor.samplePath;
      if (mediaHeader && sampleFile) {
        const expected = editor.headerFormat === 'IMAGE' ? 'image/' :
          editor.headerFormat === 'VIDEO' ? 'video/' : 'application/pdf';
        if (!sampleFile.type.startsWith(expected) || sampleFile.size > 20 * 1024 * 1024)
          throw new Error('Media must match the header type and be under 20 MB.');
        samplePath = `whatsapp-studio/${currentUser?.uid}/${Date.now()}_${sampleFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
        const uploadRef = ref(storage, samplePath);
        await uploadBytes(uploadRef, sampleFile, {contentType: sampleFile.type});
        mediaUrl = await getDownloadURL(uploadRef);
      }
      const updated = {...editor, body, mediaUrl, samplePath,
        mediaFilename: sampleFile?.name || editor.mediaFilename};
      const existing = templates.find((t) => t.name === updated.name &&
        t.language === updated.language);
      const currentStatus = existing ? (liveStatuses[`${existing.name}__${existing.language}`] ||
        submissions[`${existing.name}__${existing.language}`])?.toUpperCase() : '';
      if (existing && existing.id !== editor.id)
        throw new Error('This template name and language already have a draft. Open it above.');
      if (existing && ['APPROVED', 'PENDING'].includes(currentStatus))
        throw new Error('Approved or pending templates are locked. Copy it with a new name to make changes.');
      const target = editor.id ? doc(db, 'whatsappStudioTemplates', editor.id) :
        doc(collection(db, 'whatsappStudioTemplates'));
      await setDoc(target, {...updated, updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || ''});
      setEditor({...updated, id: target.id}); setActiveId(target.id);
      setSampleFile(null); setNotice('Draft saved. Submit it to Meta for review.');
    } catch (e: any) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const submitTemplate = async () => {
    if (!editor.id) return setError('Save the draft first.');
    setBusy(true); setError(''); setNotice('');
    try {
      const saved = templates.find((t) => t.id === editor.id);
      if (!saved || ['APPROVED', 'PENDING'].includes((liveStatuses[`${editor.name}__${editor.language}`] ||
          submissions[`${editor.name}__${editor.language}`] || '').toUpperCase()))
        throw new Error('This template is already submitted. Copy it with a new name to change it.');
      const content = (t: StudioTemplate) => JSON.stringify([t.name, t.language, t.category,
        t.headerFormat, t.headerText, t.mediaUrl, t.samplePath, t.latitude,
        t.longitude, t.locationName, t.locationAddress, t.body, t.footer, t.buttons]);
      if (content(saved) !== content(editor))
        throw new Error('Save your latest changes before submitting to Meta.');
      const components: any[] = [];
      if (editor.headerFormat === 'TEXT') components.push({type: 'HEADER', format: 'TEXT',
        text: editor.headerText, ...(editor.headerText.includes('{{1}}') ?
          {example: {header_text: ['Sample header']}} : {})});
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(editor.headerFormat)) {
        if (!editor.samplePath) throw new Error('Upload and save a sample file first.');
        components.push({type: 'HEADER', format: editor.headerFormat});
      }
      if (editor.headerFormat === 'LOCATION') components.push({type: 'HEADER', format: 'LOCATION'});
      const body: any = {type: 'BODY', text: editor.body};
      const n = validateVariables(editor.body);
      if (n) body.example = {body_text: [Array.from({length: n}, (_, i) => `Sample ${i + 1}`)]};
      components.push(body);
      if (editor.footer.trim()) components.push({type: 'FOOTER', text: editor.footer.trim()});
      if (editor.buttons.length) components.push({type: 'BUTTONS', buttons: editor.buttons.map((b) =>
        ({type: b.type, text: b.text,
          ...(b.type === 'URL' ? {url: b.url,
            ...(b.url?.includes('{{1}}') ?
              {example: [b.url.replace('{{1}}', 'sample')]} : {})} : {}),
          ...(b.type === 'PHONE_NUMBER' ? {phone_number: b.phone_number} : {})}))});
      const call = httpsCallable(functions, 'whatsappSubmitTemplate');
      const key = `${editor.name}__${editor.language}`;
      await call({name: editor.name, language: editor.language,
        category: editor.category, components, bodyText: editor.body,
        samplePath: editor.samplePath || '', studioTemplateId: editor.id,
        ...((liveStatuses[key] || submissions[key])?.toUpperCase() === 'REJECTED' &&
          submissionIds[key] ?
          {templateId: submissionIds[key]} : {})});
      setLiveStatuses((old) => ({...old, [`${editor.name}__${editor.language}`]: 'PENDING'}));
      setNotice('Submitted to Meta. Approval status will update here.');
    } catch (e: any) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const launch = async (test = false) => {
    setBusy(true); setError(''); setNotice('');
    try {
      if (!selectedTemplate || !approved) throw new Error('Choose an approved template.');
      if (!deliveryDetailsReady) throw new Error('Add the header file URL or location before sending.');
      if (!mappingsReady) throw new Error('Choose a source and provide a value for every template variable.');
      if (test) {
        const phone = phoneOf(testPhone);
        if (!phone) throw new Error('Enter a valid test number.');
        const matches = byPhone.get(phone) || [];
        const projectIds = Array.from(new Set(matches.map((m) => m.projectId).filter(Boolean)));
        const project = projectIds.length === 1 ? matches.find((m) => m.projectId === projectIds[0]) : null;
        const testRecord: Recipient = audience.find((r) => r.phone === phone) || {
          phone, name: automaticName(preferredNames(matches).names), source: 'test',
          projectId: project?.projectId || '', projectName: project?.projectName || '', fields: {},
        };
        const values = Array.from({length: count}, (_, i) => resolveMapping(testRecord, mappings[i]));
        const headerValue = selectedTemplate.headerFormat === 'TEXT' &&
          selectedTemplate.headerText.includes('{{1}}') ? resolveMapping(testRecord, headerMapping) : '';
        const buttonValues = (selectedTemplate.buttons || []).map((b, i) =>
          b.type === 'URL' && b.url?.includes('{{1}}') ?
            resolveMapping(testRecord, buttonMappings[i]) : '');
        if (values.some((value) => !value.trim()) ||
          (selectedTemplate.headerFormat === 'TEXT' && selectedTemplate.headerText.includes('{{1}}') &&
            !headerValue.trim()) ||
          (selectedTemplate.buttons || []).some((b, i) =>
            b.type === 'URL' && b.url?.includes('{{1}}') && !buttonValues[i]?.trim())) {
          throw new Error('The test number has no value for a selected variable. Add it to the recipients or choose a fixed value.');
        }
        const recipient = {phone, name: testRecord.name, projectId: testRecord.projectId,
          source: 'test', values, headerValue, buttonValues};
        const call = httpsCallable(functions, 'whatsappStudioTest');
        await call({template: selectedTemplate, recipient});
        setNotice(`Test submitted to WhatsApp for +${phone}.`);
      } else {
        if (!resolved.length || resolved.length > 1000) throw new Error('Select 1–1000 recipients.');
        if (!contactsLoaded) throw new Error('Wait for CRM contact matching to finish before sending.');
        if (invalid.length) throw new Error(`${invalid.length} recipients have unresolved names or variables.`);
        if (!consentConfirmed) throw new Error('Confirm recipients’ WhatsApp consent first.');
        const call = httpsCallable(functions, 'whatsappStudioLaunch');
        const result: any = await call({name: campaignName || selectedTemplate.name, template: selectedTemplate,
          recipients: resolved.map(({phone, name, source, projectId, values,
            headerValue, buttonValues}) =>
            ({phone, name, source, projectId, values, headerValue, buttonValues})),
          consentConfirmed,
          ...(scheduleAt ? {scheduleAt: new Date(`${scheduleAt}:00+05:30`).toISOString()} : {})});
        setNotice(scheduleAt ? 'Campaign scheduled.' : result.data?.status === 'scheduled' ?
          'Campaign queued. Marketing sends run 9 am–9 pm India time.' :
          'Campaign started. Results update below.');
      }
    } catch (e: any) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };
  const showCampaignDetails = async (campaignId: string) => {
    if (campaignDetailId === campaignId) {setCampaignDetailId(''); return;}
    try {
      const snap = await getDocs(collection(db, 'whatsappStudioCampaigns',
        campaignId, 'recipients'));
      setCampaignDetails(snap.docs.map((d) => d.data() as any));
      setCampaignDetailId(campaignId);
    } catch (e: any) {setError(String(e.message || e));}
  };

  return <Stack spacing={2} sx={studioSx}>
    <Paper sx={boxSx} elevation={0}>
      <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between" gap={1}>
        <Box><Typography variant="h6">WhatsApp Studio</Typography>
          <Typography variant="body2" color="text.secondary">Create an approved template, review recipients, test, and send or schedule.</Typography></Box>
        <Stack direction="row" gap={1}>
          <Button variant={mode === 'create' ? 'contained' : 'outlined'} onClick={() => setMode('create')}>Templates</Button>
          <Button variant={mode === 'campaign' ? 'contained' : 'outlined'} onClick={() => setMode('campaign')}>Campaign</Button>
        </Stack>
      </Stack>
    </Paper>
    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
    {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
    {mode === 'create' ? <>
      <Paper sx={boxSx} elevation={0}>
        <Stack direction="row" gap={1} flexWrap="wrap" sx={{mb: 2}}>
          <Button onClick={() => {setEditor(blankTemplate()); setSampleFile(null);}}>New template</Button>
          {editor.id && <Button onClick={() => {setEditor({...editor, id: undefined,
            name: `${editor.name}_v2`,
            ...(mediaHeader ? {samplePath: undefined, mediaUrl: '', mediaFilename: ''} : {})});
            setSampleFile(null);}}>Copy as new</Button>}
          {templates.map((t) => <Chip key={t.id} clickable
            label={`${t.name} · ${LANG[t.language]} · ${liveStatuses[`${t.name}__${t.language}`] || submissions[`${t.name}__${t.language}`] || 'Draft'}`}
            onClick={() => {setEditor(t); setSampleFile(null);}} />)}
        </Stack>
        <Stack spacing={2}>
          <Stack direction={{xs: 'column', sm: 'row'}} gap={1} alignItems="flex-start">
            <TextField label="Meta template name" value={editor.name} fullWidth disabled={!!editor.id}
              helperText="Lowercase letters, numbers and underscores"
              onChange={(e) => setEditor({...editor, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})} />
            <Select value={editor.language} sx={{height: 56, minWidth: 110}}
              onChange={(e) => setEditor({...editor, language: e.target.value as Language})}>
              {Object.entries(LANG).map(([code, label]) => <MenuItem key={code} value={code}>{label}</MenuItem>)}
            </Select>
            <Select value={editor.category} sx={{height: 56, minWidth: 132}}
              onChange={(e) => setEditor({...editor, category: e.target.value as StudioTemplate['category']})}>
              <MenuItem value="MARKETING">Marketing</MenuItem><MenuItem value="UTILITY">Utility</MenuItem>
            </Select>
          </Stack>
          <Typography fontWeight={600}>Header</Typography>
          <Select value={editor.headerFormat} onChange={(e) => setEditor({...editor, headerFormat: e.target.value as HeaderFormat})}>
            {(['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'LOCATION'] as HeaderFormat[]).map((h) =>
              <MenuItem key={h} value={h}>{h === 'NONE' ? 'No header' : h.toLowerCase()}</MenuItem>)}
          </Select>
          {editor.headerFormat === 'TEXT' && <TextField label="Header text" value={editor.headerText}
            onChange={(e) => setEditor({...editor, headerText: e.target.value})} inputProps={{maxLength: 60}} />}
          {mediaHeader && <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Button component="label" variant="outlined">Upload {editor.headerFormat.toLowerCase()} sample
              <input hidden type="file" accept={editor.headerFormat === 'IMAGE' ? 'image/*' :
                editor.headerFormat === 'VIDEO' ? 'video/*' : 'application/pdf'}
              onChange={(e) => setSampleFile(e.target.files?.[0] || null)} /></Button>
            <Typography variant="body2">{sampleFile?.name || editor.mediaFilename || 'No file selected'}</Typography>
            <Typography variant="caption" color="text.secondary">This file is used for Meta review and sends.</Typography>
          </Stack>}
          {editor.headerFormat === 'LOCATION' && <Stack direction={{xs: 'column', sm: 'row'}} gap={1}>
            {(['latitude', 'longitude', 'locationName', 'locationAddress'] as const).map((key) =>
              <TextField key={key} label={key} value={editor[key]} fullWidth
                onChange={(e) => setEditor({...editor, [key]: e.target.value})} />)}
          </Stack>}
          <Divider />
          <Typography fontWeight={600}>Message</Typography>
          <TextField label="Body" multiline minRows={6} value={editor.body}
            onChange={(e) => setEditor({...editor, body: e.target.value})}
            helperText={`${editor.body.length}/1024 · Use {{1}}, {{2}} for variables. WhatsApp uses *bold*, _italic_, ~strike~, and monospace with three backticks.`} />
          <Stack direction="row" gap={1} flexWrap="wrap">
            {[['Bold', '*text*'], ['Italic', '_text_'], ['Strike', '~text~'],
              ['Monospace', '```text```'], ['Inline code', '`text`'],
              ['Bullet', '\n- item'], ['Numbered', '\n1. item'],
              ['Quote', '\n> text'], ['Variable', `{{${variableCount(editor.body) + 1}}}`]]
              .map(([label, syntax]) =>
              <Button key={label} size="small" variant="outlined"
                onClick={() => setEditor({...editor, body: `${editor.body}${editor.body ? ' ' : ''}${syntax}`})}>{label}</Button>)}
          </Stack>
          <TextField label="Footer (optional)" value={editor.footer} inputProps={{maxLength: 60}}
            onChange={(e) => setEditor({...editor, footer: e.target.value})} />
          <Typography fontWeight={600}>Buttons</Typography>
          {editor.buttons.map((b, i) => <Stack key={i} direction={{xs: 'column', sm: 'row'}} gap={1}>
            <Select value={b.type} onChange={(e) => setEditor({...editor, buttons: editor.buttons.map((row, j) =>
              j === i ? {...row, type: e.target.value as StudioButton['type']} : row)})}>
              <MenuItem value="QUICK_REPLY">Quick reply</MenuItem><MenuItem value="URL">Website URL</MenuItem>
              <MenuItem value="PHONE_NUMBER">Call phone</MenuItem>
            </Select>
            <TextField label="Button label" value={b.text} fullWidth onChange={(e) => setEditor({...editor,
              buttons: editor.buttons.map((row, j) => j === i ? {...row, text: e.target.value} : row)})} />
            {b.type !== 'QUICK_REPLY' && <TextField label={b.type === 'URL' ? 'HTTPS URL' : 'Phone number'}
              value={b.type === 'URL' ? b.url || '' : b.phone_number || ''} fullWidth
              onChange={(e) => setEditor({...editor, buttons: editor.buttons.map((row, j) =>
                j === i ? {...row, [b.type === 'URL' ? 'url' : 'phone_number']: e.target.value} : row)})} />}
            <Button color="error" onClick={() => setEditor({...editor, buttons: editor.buttons.filter((_, j) => j !== i)})}>Remove</Button>
          </Stack>)}
          <Button sx={{alignSelf: 'flex-start'}} onClick={() => setEditor({...editor,
            buttons: [...editor.buttons, {type: 'QUICK_REPLY', text: ''}]})}>Add button</Button>
          <Box sx={{p: 2, bgcolor: '#e9f4e5', borderRadius: 2, whiteSpace: 'pre-wrap', maxWidth: 450}}>
            {editor.headerText && <Typography fontWeight={700}>{editor.headerText}</Typography>}
            {editor.headerFormat === 'IMAGE' && editor.mediaUrl &&
              <Box component="img" src={editor.mediaUrl} alt="Template header preview"
                sx={{width: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 1}} />}
            {editor.headerFormat === 'VIDEO' && editor.mediaUrl &&
              <Box component="video" src={editor.mediaUrl} controls
                sx={{width: '100%', maxHeight: 240, borderRadius: 1}} />}
            {editor.headerFormat === 'DOCUMENT' && editor.mediaFilename &&
              <Typography variant="body2">📄 {editor.mediaFilename}</Typography>}
            {editor.headerFormat === 'LOCATION' && editor.locationName &&
              <Typography variant="body2">📍 {editor.locationName} · {editor.locationAddress}</Typography>}
            <Typography component="div">{renderWhatsApp(editor.body || 'Message preview')}</Typography>
            {editor.footer && <Typography variant="caption">{editor.footer}</Typography>}
            {editor.buttons.map((button, i) => <Box key={i} sx={{mt: 1, py: 0.5,
              textAlign: 'center', color: '#27649c', borderTop: '1px solid #cfe0c9'}}>
              {button.text || 'Button'}
            </Box>)}
          </Box>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" disabled={busy} onClick={saveTemplate}>Save draft</Button>
            <Button variant="contained" disabled={busy || !editor.id} onClick={submitTemplate}>Submit to Meta</Button>
          </Stack>
        </Stack>
      </Paper>
    </> : <>
      <Paper sx={boxSx} elevation={0}>
        <Stack spacing={2}>
          <Typography variant="h6">1. Approved template</Typography>
          <Select value={selectedTemplate?.id || ''} displayEmpty onChange={(e) => {
            setActiveId(String(e.target.value)); setMappings([]);
            setHeaderMapping(blankMapping()); setButtonMappings({}); }}>
            <MenuItem value="">Choose approved template</MenuItem>
            {approvedTemplates.map((t) => <MenuItem key={t.id} value={t.id}>
              {t.name} · {LANG[t.language]}
            </MenuItem>)}
          </Select>
          {!approvedTemplates.length && <Typography variant="body2" color="text.secondary">
            No Studio templates are approved yet. Check approval in Studio Templates.
          </Typography>}
          {selectedTemplate && <Typography variant="body2" sx={{whiteSpace: 'pre-wrap'}}>{selectedTemplate.body}</Typography>}
          <TextField label="Campaign name" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
        </Stack>
      </Paper>
      <Paper sx={boxSx} elevation={0}>
        <Stack spacing={2}>
          <Typography variant="h6">2. Recipients</Typography>
          {!contactsLoaded && <Button sx={{alignSelf: 'flex-start'}} variant="outlined"
            onClick={() => loadContacts().catch((e) => setError(String(e.message || e)))}>
            Retry CRM contact matching</Button>}
          <Select value={audienceSource} onChange={(e) => {
            setAudienceSource(e.target.value as typeof audienceSource);
            setSelectedPhones({}); setNameChoices({});
            setContactTypeFilter(''); setConsentConfirmed(false); }}>
            <MenuItem value="csv">Upload CSV</MenuItem><MenuItem value="lead">CRM leads</MenuItem>
            <MenuItem value="buyer">Booked buyers</MenuItem><MenuItem value="broker">Brokers</MenuItem>
            <MenuItem value="contact-book">Office Contact Book</MenuItem>
          </Select>
          {audienceSource === 'csv' ? <>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Button component="label" variant="outlined">Choose CSV
                <input hidden type="file" accept=".csv,text/csv" onChange={(e) => {
                  const file = e.target.files?.[0]; if (file) onCsv(file); }} /></Button>
              <Button variant="text" onClick={downloadSampleCsv}>Download sample CSV</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Use real phone numbers. Name is optional: Studio uses a unit customer, Contact Book, CSV name, or “there”, in that order. Add columns for message variables as needed.
            </Typography>
            {csvHeaders.length > 0 && <Stack direction="row" gap={1}>
              <Select value={phoneColumn} onChange={(e) => changeCsvColumns(String(e.target.value), nameColumn)}>
                {csvHeaders.map((h) => <MenuItem key={h} value={h}>Phone: {h}</MenuItem>)}
              </Select>
              <Select value={nameColumn} onChange={(e) => changeCsvColumns(phoneColumn, String(e.target.value))}>
                <MenuItem value="">No CSV name column</MenuItem>
                {csvHeaders.map((h) => <MenuItem key={h} value={h}>Name: {h}</MenuItem>)}
              </Select>
            </Stack>}
            <Typography variant="body2">{csvRows.length} distinct valid numbers · {Math.max(0, csvTable.length - 1 - csvRows.length)} invalid or duplicate rows skipped. {contactsLoaded ? 'Unit customers and Contact Book supply names first; different first names at the same priority need your choice.' : 'CRM name matching is still loading.'}</Typography>
            <Box sx={{maxHeight: 350, overflow: 'auto'}}><Table size="small"><TableHead><TableRow>
              <TableCell>CSV row / number</TableCell><TableCell>CSV name</TableCell><TableCell>Preferred matches</TableCell><TableCell>Name to send</TableCell>
            </TableRow></TableHead><TableBody>{csvPreview.map((r) => <TableRow key={r.phone}>
              <TableCell>{r.row} · +{r.phone}</TableCell><TableCell>{r.csvName || '—'}</TableCell>
              <TableCell>{r.names.length ? r.names.map((name) => `${name} (${r.source})`).join('; ') : 'No unit or Contact Book match'}</TableCell>
              <TableCell><Select size="small" displayEmpty value={nameChoices[r.phone] || ''}
                onChange={(e) => setNameChoices({...nameChoices, [r.phone]: String(e.target.value)})}>
                <MenuItem value="">{r.autoName ? `Auto: ${r.autoName}` : 'Choose a unit or Contact Book name'}</MenuItem>
                {r.names.map((name) => <MenuItem key={name} value={name}>{firstName(name)} · {r.source}</MenuItem>)}
                {r.csvName && !!r.autoName && !r.names.includes(r.csvName) &&
                  <MenuItem value={r.csvName}>{firstName(r.csvName)} · CSV</MenuItem>}
              </Select></TableCell>
            </TableRow>)}</TableBody></Table></Box>
          </> : <>
            <Select value={projectFilter} displayEmpty onChange={(e) => setProjectFilter(String(e.target.value))}>
              <MenuItem value="">All projects</MenuItem>
              {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </Select>
            {['lead', 'contact-book'].includes(audienceSource) && <Select value={contactTypeFilter} displayEmpty
              onChange={(e) => setContactTypeFilter(String(e.target.value))}>
              <MenuItem value="">{audienceSource === 'lead' ? 'All lead statuses' : 'All contact types'}</MenuItem>
              {Array.from(new Set(contacts.filter((c) => c.source === audienceSource)
                .map((c) => c.status).filter(Boolean))).map((type) =>
                <MenuItem key={type} value={type}>{type}</MenuItem>)}
            </Select>}
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">{new Set(availableContacts.map((c) => c.phone)).size} available · {audience.length} selected</Typography>
              <Button onClick={() => setSelectedPhones((old) => {
                const next = {...old}; availableContacts.forEach((c) => {next[c.phone] = true;});
                return next;
              })}>Select all shown</Button>
              <Button disabled={!audience.length} onClick={() => setSelectedPhones((old) => {
                const next = {...old}; availableContacts.forEach((c) => {delete next[c.phone];});
                return next;
              })}>Clear shown</Button>
            </Stack>
            <Box sx={{maxHeight: 320, overflow: 'auto'}}>{availableContacts
              .map((c) => <FormControlLabel key={`${c.source}:${c.id}`}
                sx={{display: 'flex'}} control={<Checkbox checked={!!selectedPhones[c.phone]}
                  onChange={(e) => setSelectedPhones({...selectedPhones, [c.phone]: e.target.checked})} />}
                label={`${c.name} · +${c.phone}${c.projectName ? ` · ${c.projectName}` : ''}`} />)}</Box>
            {contactsLoaded && !availableContacts.length &&
              <Typography variant="body2" color="text.secondary">No contacts match this list and filter.</Typography>}
            <Box>
              <Typography sx={{fontWeight: 600, mb: 1}}>Recipients to send ({audience.length})</Typography>
              {audience.length ? <Box sx={{maxHeight: 320, overflow: 'auto', border: `1px solid ${UI.line}`, borderRadius: 1.5}}>
                <Table size="small" aria-label="Selected recipients preview">
                  <TableHead><TableRow><TableCell padding="checkbox">Send</TableCell><TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell><TableCell>Project</TableCell></TableRow></TableHead>
                  <TableBody>{audience.map((r) => <TableRow key={r.phone} hover>
                    <TableCell padding="checkbox"><Checkbox checked aria-label={`Send to ${r.name || r.phone}`}
                      onChange={() => setSelectedPhones((old) => ({...old, [r.phone]: false}))} /></TableCell>
                    <TableCell>{r.name || 'Choose name below'}</TableCell><TableCell>+{r.phone}</TableCell>
                    <TableCell>{r.projectName || '—'}</TableCell>
                  </TableRow>)}</TableBody>
                </Table>
              </Box> : <Typography variant="body2" color="text.secondary">Select contacts above to review them here.</Typography>}
            </Box>
            {selectedConflicts.map((r) => <Stack key={r.phone} direction="row" gap={1} alignItems="center">
              <Typography>Shared +{r.phone}: choose name</Typography>
              <Select size="small" displayEmpty value={nameChoices[r.phone] || ''}
                onChange={(e) => setNameChoices({...nameChoices, [r.phone]: String(e.target.value)})}>
                <MenuItem value="">Choose name</MenuItem>
                {r.names.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </Select>
            </Stack>)}
          </>}
        </Stack>
      </Paper>
      <Paper sx={boxSx} elevation={0}><Stack spacing={2}>
        <Typography variant="h6">3. Variables and review</Typography>
        {Array.from({length: count}, (_, i) => <React.Fragment key={i}>
          {mappingEditor(`Body {{${i + 1}}}`, mappings[i] || blankMapping(),
            (next) => setMappings((old) => {const copy = [...old]; copy[i] = next; return copy;}),
            isGiftCollectionTemplate && i === 0 ? 'Collection deadline for all' : 'Value')}
        </React.Fragment>)}
        {selectedTemplate?.headerFormat === 'TEXT' && selectedTemplate.headerText.includes('{{1}}') &&
          mappingEditor('Header {{1}}', headerMapping, setHeaderMapping)}
        {(selectedTemplate?.buttons || []).map((b, i) => b.type === 'URL' && b.url?.includes('{{1}}') ?
          <React.Fragment key={i}>{mappingEditor(`URL ${i + 1}`, buttonMappings[i] || blankMapping(),
          (next) => setButtonMappings((old) => ({...old, [i]: next})))}</React.Fragment> : null)}
        <Typography>{resolved.length} recipients · {invalid.length} need names or variable values · {Math.max(0, resolved.length - invalid.length)} ready</Typography>
        {!mappingsReady && <Typography color="text.secondary" variant="body2">
          Choose a source for every variable before testing or sending.
        </Typography>}
        <FormControlLabel control={<Checkbox checked={consentConfirmed}
          onChange={(e) => setConsentConfirmed(e.target.checked)} />}
          label="I confirm these recipients agreed to receive this WhatsApp message." />
        <Box sx={{maxHeight: 250, overflow: 'auto'}}><Table size="small"><TableHead><TableRow>
          <TableCell>Number</TableCell><TableCell>Name</TableCell><TableCell>Source</TableCell><TableCell>Variables</TableCell><TableCell>Ready</TableCell>
        </TableRow></TableHead><TableBody>{resolved.slice(0, 100).map((r) => <TableRow key={r.phone}>
          <TableCell>+{r.phone}</TableCell><TableCell>{r.name || '—'}</TableCell><TableCell>{r.source}</TableCell>
          <TableCell>{r.values.join(' · ')}</TableCell><TableCell>{r.name && r.values.every(Boolean) ? 'Yes' : 'No'}</TableCell>
        </TableRow>)}</TableBody></Table></Box>
        {resolved.length > 100 && <Typography variant="caption">Showing first 100; all {resolved.length} are included.</Typography>}
        <Stack direction={{xs: 'column', sm: 'row'}} gap={1} alignItems="center">
          <TextField label="Test phone" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} />
          <Button disabled={busy || !approved || !deliveryDetailsReady || !mappingsReady} variant="outlined" sx={{height: 56}}
            onClick={() => launch(true)}>Send test</Button>
          <TextField label="Schedule (India time)" type="datetime-local" value={scheduleAt}
            InputLabelProps={{shrink: true}} onChange={(e) => setScheduleAt(e.target.value)} />
          <Button disabled={busy || !approved || !deliveryDetailsReady || !mappingsReady || !contactsLoaded || !resolved.length || !!invalid.length ||
            !consentConfirmed} variant="contained" sx={{height: 56}} onClick={() => launch(false)}>
            {scheduleAt ? 'Schedule campaign' : 'Send now'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">Marketing sends run from 9 am to 9 pm India time and skip contacts messaged for marketing in the last 3 days.</Typography>
      </Stack></Paper>
      <Paper sx={boxSx} elevation={0}><Typography variant="h6">Campaigns</Typography>
        {campaigns.map((c) => <Box key={c.id} sx={{py: 1, borderBottom: '1px solid #eee'}}>
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography sx={{minWidth: 200}}>{c.name}</Typography><Chip size="small" label={c.status} />
            <Typography variant="body2">{c.sentCount || 0} sent · {c.failedCount || 0} failed · {c.skippedCount || 0} skipped / {c.recipientCount}</Typography>
            <Button size="small" onClick={() => showCampaignDetails(c.id)}>
              {campaignDetailId === c.id ? 'Hide results' : 'View results'}</Button>
            {c.status === 'paused' && <Button size="small" onClick={async () => {
              try {await httpsCallable(functions, 'whatsappStudioResume')({campaignId: c.id});}
              catch (e: any) {setError(String(e.message || e));}
            }}>Resume</Button>}
            {['scheduled', 'running', 'paused'].includes(c.status) && <Button size="small" color="error" onClick={async () => {
              try {await httpsCallable(functions, 'whatsappStudioCancel')({campaignId: c.id});}
              catch (e: any) {setError(String(e.message || e));}
            }}>Cancel remaining</Button>}
          </Stack>
          {campaignDetailId === c.id && <Box sx={{maxHeight: 280, overflow: 'auto'}}>
            <Table size="small"><TableHead><TableRow><TableCell>Number</TableCell><TableCell>Name</TableCell>
              <TableCell>Status</TableCell><TableCell>Result</TableCell></TableRow></TableHead>
              <TableBody>{campaignDetails.map((r) => <TableRow key={r.phone}>
                <TableCell>+{r.phone}</TableCell><TableCell>{r.name}</TableCell>
                <TableCell>{r.status}</TableCell><TableCell>{r.error || r.waMessageId || '—'}</TableCell>
              </TableRow>)}</TableBody></Table>
          </Box>}
        </Box>)}
      </Paper>
    </>}
  </Stack>;
}
